import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Timer, CheckCircle, Copy, PencilSimple, Trash, ArrowLeft, ArrowUp, X } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export const STATUS_STYLE = {
  green: "bg-[#A9C97E]/15 text-[#A9C97E]",
  amber: "bg-[#F5A623]/15 text-[#F5A623]",
  red: "bg-[#F87171]/15 text-[#F87171]",
};
export const STATUS_LABEL = { green: "On track", amber: "Running behind", red: "Significantly behind" };

export const fmtMin = (m) => {
  if (m == null) return "—";
  const sign = m < 0 ? "−" : "";
  const a = Math.abs(Math.round(m));
  return `${sign}${a >= 60 ? `${Math.floor(a / 60)}h ` : ""}${a % 60}m`;
};
export const fmtDrift = (d) => (d > 0 ? `+${fmtMin(d)}` : d < 0 ? fmtMin(d) : "0m");

const EMPTY_COURSE = { name: "", eat_min: "", transition_min: "" };

function OpenTableDialog({ presets, onClose, onOpened }) {
  const [label, setLabel] = useState("");
  const [presetId, setPresetId] = useState(presets[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      await api.post("/pacing/board/open", { label, preset_id: presetId });
      toast.success(`Table ${label} opened — course 1 timer running`);
      onOpened();
    } catch (e) {
      toast.error(apiError(e, "Couldn't open table"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="passe-open-dialog">
        <DialogHeader><DialogTitle className="font-heading">Open table</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Table label / number</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. T4" data-testid="passe-open-label" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Menu preset</Label>
            <Select value={presetId} onValueChange={setPresetId}>
              <SelectTrigger data-testid="passe-open-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.courses.length} courses · {fmtMin(p.total_min)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={open} disabled={busy || !label.trim() || !presetId} data-testid="passe-open-submit"
            className="w-full bg-primary hover:bg-[#8B5CF6] text-white">
            {busy ? "Opening…" : "Open table — start course 1"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TableDetail({ id, onBack, onChanged }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/pacing/tables/${id}`).then((r) => setT(r.data)).catch(() => toast.error("Failed to load table"));
  useEffect(() => {
    load();
    const tmr = setInterval(load, 15000);
    return () => clearInterval(tmr);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (path, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pacing/tables/${id}/${path}`);
      setT((p) => ({ ...p, ...data }));
      if (okMsg) toast.success(okMsg);
      onChanged();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };
  const voidTable = async () => {
    try {
      await api.delete(`/pacing/tables/${id}`);
      toast.success("Table voided");
      onChanged();
      onBack();
    } catch (e) {
      toast.error(apiError(e, "Void failed"));
    }
  };

  if (!t) return <p className="text-muted-foreground">Loading…</p>;
  const st = t.state;
  const allServed = st.current_idx === null;
  return (
    <div className="space-y-5" data-testid="passe-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="passe-detail-back"><ArrowLeft size={16} className="mr-1" /> Board</Button>
        <h2 className="font-heading text-2xl font-bold">Table {t.label}</h2>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[st.status]}`} data-testid="passe-detail-status">{STATUS_LABEL[st.status]}</span>
        {t.status === "open" && !t.courses_log?.length && (
          <Button variant="ghost" size="sm" className="text-destructive ml-auto" onClick={voidTable} data-testid="passe-void-btn">Void table</Button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["Elapsed", fmtMin(st.elapsed_min), "passe-elapsed"],
          ["Forecast total", fmtMin(st.total_forecast_min), "passe-forecast"],
          ["Current course", st.current_pos, "passe-current-pos"],
          ["Drift vs forecast", fmtDrift(st.drift_min), "passe-drift"],
        ].map(([l, v, tid]) => (
          <Card key={tid} className="border-border shadow-none" data-testid={tid}>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-muted-foreground">{l}</p>
              <p className="font-heading text-xl font-bold mt-0.5">{v}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      {t.status === "open" && (
        <div className="glass border border-border rounded-lg p-5 space-y-3">
          {allServed ? (
            <Button onClick={() => act("close", "Table closed — totals logged")} disabled={busy} data-testid="passe-close-btn"
              className="w-full h-14 text-lg bg-[#A9C97E] hover:bg-[#8FA98C] text-black font-heading">
              <CheckCircle size={22} className="mr-2" /> Close table — log seating
            </Button>
          ) : (
            <>
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                  <p className="text-xs text-muted-foreground">Now serving</p>
                  <p className="font-heading text-xl font-bold" data-testid="passe-now-serving">Course {st.current_pos} — {st.current_name}</p>
                </div>
                <p className="text-sm text-muted-foreground num">{fmtMin(st.elapsed_on_course)} on course · target {fmtMin(st.course_target)}</p>
              </div>
              <Button onClick={() => act("serve")} disabled={busy} data-testid="passe-serve-btn"
                className="w-full h-14 text-lg bg-primary hover:bg-[#8B5CF6] text-white font-heading">
                <CheckCircle size={22} className="mr-2" /> Course served — next
              </Button>
            </>
          )}
        </div>
      )}
      <div className="glass border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="passe-course-table">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-4 py-2.5 font-medium">#</th>
              <th className="px-4 py-2.5 font-medium">Course</th>
              <th className="px-4 py-2.5 font-medium text-right">Target step</th>
              <th className="px-4 py-2.5 font-medium text-right">Served at</th>
              <th className="px-4 py-2.5 font-medium text-right">Actual</th>
              <th className="px-4 py-2.5 font-medium text-right">Variance</th>
            </tr>
          </thead>
          <tbody>
            {st.courses.map((c) => (
              <tr key={c.idx} data-testid={`passe-course-${c.idx}`}
                className={`border-b last:border-0 ${c.idx === st.current_idx && t.status === "open" ? "bg-primary/10" : ""}`}>
                <td className="px-4 py-2.5 num">{c.idx + 1}</td>
                <td className="px-4 py-2.5 font-medium">{c.name}</td>
                <td className="px-4 py-2.5 num text-right">{fmtMin(c.step_target)}</td>
                <td className="px-4 py-2.5 num text-right">{c.served_at ? new Date(c.served_at).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                <td className="px-4 py-2.5 num text-right">{c.actual_min != null ? fmtMin(c.actual_min) : "—"}</td>
                <td className={`px-4 py-2.5 num text-right font-medium ${c.variance_min > 0 ? "text-[#F5A623]" : "text-[#A9C97E]"}`}
                  data-testid={`passe-variance-${c.idx}`}>
                  {c.variance_min != null ? fmtDrift(c.variance_min) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Board() {
  const [tables, setTables] = useState(null);
  const [presets, setPresets] = useState([]);
  const [showOpen, setShowOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const load = () => api.get("/pacing/board").then((r) => setTables(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/pacing-presets").then((r) => setPresets(r.data)).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (detailId) return <TableDetail id={detailId} onBack={() => setDetailId(null)} onChanged={load} />;

  return (
    <div className="space-y-4" data-testid="passe-board">
      <div className="flex justify-end">
        <Button onClick={() => setShowOpen(true)} data-testid="passe-open-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
          <Plus size={16} className="mr-1" /> Open table
        </Button>
      </div>
      {tables && tables.length === 0 && (
        <div className="glass border border-border rounded-lg p-10 text-center text-muted-foreground" data-testid="passe-board-empty">
          No open tables. Open one when a seating starts — the timer begins at course 1.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(tables || []).map((t) => (
          <button key={t.id} onClick={() => setDetailId(t.id)} data-testid={`passe-card-${t.id}`}
            className="glass border border-border rounded-xl p-5 text-left hover:border-primary/50 transition-colors space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-heading text-2xl font-bold">{t.label}</p>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[t.state.status]}`} data-testid={`passe-status-${t.id}`}>
                {STATUS_LABEL[t.state.status]}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{t.preset_snapshot.name}</p>
            <div>
              <p className="text-sm font-medium" data-testid={`passe-course-now-${t.id}`}>
                {t.state.current_idx === null ? "All courses served" : `Course ${t.state.current_pos} — ${t.state.current_name}`}
              </p>
              <p className="text-xs text-muted-foreground num mt-0.5">
                Elapsed {fmtMin(t.state.elapsed_min)} · forecast {fmtMin(t.state.total_forecast_min)} · drift {fmtDrift(t.state.drift_min)}
              </p>
            </div>
          </button>
        ))}
      </div>
      {showOpen && <OpenTableDialog presets={presets} onClose={() => setShowOpen(false)} onOpened={() => { setShowOpen(false); load(); }} />}
    </div>
  );
}

function PresetBuilder({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [courses, setCourses] = useState(initial?.courses?.map((c) => ({ ...c })) || [{ ...EMPTY_COURSE }]);
  const [busy, setBusy] = useState(false);
  const total = courses.reduce((a, c, i) => a + (Number(c.eat_min) || 0) + (i < courses.length - 1 ? Number(c.transition_min) || 0 : 0), 0);
  const setC = (i, k, v) => setCourses(courses.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const move = (i) => { if (i === 0) return; const n = [...courses]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setCourses(n); };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { name, courses: courses.map((c) => ({ name: c.name, eat_min: Number(c.eat_min) || 0, transition_min: Number(c.transition_min) || 0 })) };
      if (initial?.id) await api.put(`/pacing-presets/${initial.id}`, payload);
      else await api.post("/pacing-presets", payload);
      toast.success("Preset saved");
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="passe-preset-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit preset" : "New menu preset"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Preset name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 10-Course Tasting" data-testid="passe-preset-name" />
          </div>
          <div className="grid grid-cols-[28px_1fr_90px_110px_64px] gap-2 text-xs text-muted-foreground px-0.5">
            <span /><span>Course / dish</span><span>Eat (min)</span><span>Transition (min)</span><span />
          </div>
          {courses.map((c, i) => (
            <div key={i} className="grid grid-cols-[28px_1fr_90px_110px_64px] gap-2 items-center" data-testid={`passe-course-row-${i}`}>
              <span className="text-xs text-muted-foreground num">{i + 1}</span>
              <Input value={c.name} onChange={(e) => setC(i, "name", e.target.value)} placeholder="Dish name" data-testid={`passe-course-name-${i}`} />
              <Input type="number" min="0" value={c.eat_min} onChange={(e) => setC(i, "eat_min", e.target.value)} data-testid={`passe-course-eat-${i}`} />
              <Input type="number" min="0" value={c.transition_min} onChange={(e) => setC(i, "transition_min", e.target.value)}
                placeholder={i === courses.length - 1 ? "n/a (last)" : ""} disabled={i === courses.length - 1} data-testid={`passe-course-trans-${i}`} />
              <div className="flex gap-0.5">
                <Button size="sm" variant="ghost" onClick={() => move(i)} disabled={i === 0} data-testid={`passe-course-up-${i}`}><ArrowUp size={13} /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setCourses(courses.filter((_, j) => j !== i))}
                  disabled={courses.length === 1} data-testid={`passe-course-del-${i}`}><X size={13} /></Button>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between border-t pt-3">
            <Button size="sm" variant="outline" onClick={() => setCourses([...courses, { ...EMPTY_COURSE }])} data-testid="passe-course-add">
              <Plus size={14} className="mr-1" /> Add course
            </Button>
            <p className="text-sm" data-testid="passe-preset-total">
              Total: <span className="font-heading font-bold">{fmtMin(total)}</span>
              <span className="text-muted-foreground"> ({courses.length} courses)</span>
            </p>
          </div>
          <Button onClick={save} disabled={busy || !name.trim() || !courses.some((c) => c.name.trim())} data-testid="passe-preset-save"
            className="w-full bg-primary hover:bg-[#8B5CF6] text-white">{busy ? "Saving…" : "Save preset"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Presets() {
  const [presets, setPresets] = useState(null);
  const [cfg, setCfg] = useState({ amber_min: 5, red_min: 10 });
  const [edit, setEdit] = useState(null);
  const load = () => {
    api.get("/pacing-presets").then((r) => setPresets(r.data)).catch(() => toast.error("Failed to load presets"));
    api.get("/pacing/config").then((r) => setCfg(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const saveCfg = async () => {
    try {
      await api.put("/pacing/config", { amber_min: Number(cfg.amber_min), red_min: Number(cfg.red_min) });
      toast.success("Thresholds saved");
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };
  const clone = async (p) => {
    try {
      await api.post(`/pacing-presets/${p.id}/clone`);
      toast.success(`Cloned as "${p.name} (copy)"`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Clone failed"));
    }
  };
  const archive = async (p) => {
    try {
      await api.delete(`/pacing-presets/${p.id}`);
      toast.success("Preset archived");
      load();
    } catch (e) {
      toast.error(apiError(e, "Archive failed"));
    }
  };
  return (
    <div className="space-y-4" data-testid="passe-presets">
      <div className="flex justify-end">
        <Button onClick={() => setEdit({})} data-testid="passe-preset-new" className="bg-primary hover:bg-[#8B5CF6] text-white">
          <Plus size={16} className="mr-1" /> New preset
        </Button>
      </div>
      <Card className="border-border shadow-none">
        <CardContent className="p-0">
          {(presets || []).map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-0" data-testid={`passe-preset-row-${p.id}`}>
              <div>
                <p className="font-medium text-sm">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.courses.length} courses · total {fmtMin(p.total_min)}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => clone(p)} data-testid={`passe-preset-clone-${p.id}`}><Copy size={14} /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEdit(p)} data-testid={`passe-preset-edit-${p.id}`}><PencilSimple size={14} /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => archive(p)} data-testid={`passe-preset-del-${p.id}`}><Trash size={14} /></Button>
              </div>
            </div>
          ))}
          {presets && presets.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="passe-presets-empty">No presets yet — build your tasting menu to start pacing tables.</p>
          )}
        </CardContent>
      </Card>
      <Card className="border-border shadow-none" data-testid="passe-thresholds">
        <CardHeader><CardTitle className="font-heading text-base">Behind-schedule thresholds</CardTitle></CardHeader>
        <CardContent className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5">
            <Label className="text-xs">Amber at (min over forecast)</Label>
            <Input type="number" min="1" className="w-28" value={cfg.amber_min} onChange={(e) => setCfg({ ...cfg, amber_min: e.target.value })} data-testid="passe-amber" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Red at (min over forecast)</Label>
            <Input type="number" min="1" className="w-28" value={cfg.red_min} onChange={(e) => setCfg({ ...cfg, red_min: e.target.value })} data-testid="passe-red" />
          </div>
          <Button variant="outline" onClick={saveCfg} data-testid="passe-thresholds-save">Save thresholds</Button>
        </CardContent>
      </Card>
      {edit !== null && <PresetBuilder initial={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function History() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/pacing/history?date=${date}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [date]);
  return (
    <div className="space-y-4" data-testid="passe-history">
      <div className="space-y-1.5 max-w-[200px]">
        <Label className="text-xs">Service date</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="passe-history-date" />
      </div>
      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-4 py-2.5 font-medium">Table</th>
              <th className="px-4 py-2.5 font-medium">Menu</th>
              <th className="px-4 py-2.5 font-medium text-right">Forecast</th>
              <th className="px-4 py-2.5 font-medium text-right">Actual</th>
              <th className="px-4 py-2.5 font-medium text-right">Difference</th>
              <th className="px-4 py-2.5 font-medium">Biggest variance</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`passe-history-row-${r.id}`}>
                <td className="px-4 py-2.5 font-medium">{r.label}</td>
                <td className="px-4 py-2.5">{r.preset_snapshot?.name}</td>
                <td className="px-4 py-2.5 num text-right">{fmtMin(r.total_forecast_min)}</td>
                <td className="px-4 py-2.5 num text-right">{fmtMin(r.total_actual_min)}</td>
                <td className={`px-4 py-2.5 num text-right font-medium ${r.total_actual_min > r.total_forecast_min ? "text-[#F5A623]" : "text-[#A9C97E]"}`}>
                  {fmtDrift((r.total_actual_min || 0) - (r.total_forecast_min || 0))}
                </td>
                <td className="px-4 py-2.5 text-xs">{r.biggest_variance ? `${r.biggest_variance.course} (${fmtDrift(r.biggest_variance.variance_min)})` : "—"}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground" data-testid="passe-history-empty">No closed tables on this date.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Passe() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("ptab") || "board";
  if (!can("run_passe")) return <p className="text-muted-foreground" data-testid="passe-no-perm">You don't have permission to run the passé board.</p>;
  const TABS = [["board", "Board"], ["presets", "Menu presets"], ["history", "History"]];
  return (
    <div className="space-y-6" data-testid="passe-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">MISE Passé</h1>
        <p className="text-muted-foreground mt-1">Course pacing for tasting-menu service — open tables, tap through courses, get warned when a table runs behind.</p>
      </div>
      <div className="flex gap-1 border-b border-border" data-testid="passe-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setParams({ ptab: k })} data-testid={`passe-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "board" && <Board />}
      {tab === "presets" && <Presets />}
      {tab === "history" && <History />}
    </div>
  );
}
